/**
 * InAppInteractionOverlay — renders post-auth DXCUserInteraction as a
 * modal overlay ON TOP of the app, instead of replacing it.
 *
 * Used for: message-alert, logout-confirmation, generic interactions
 * that fire while the user is already authenticated and using the app.
 *
 * Pre-auth interactions (email, otp) still use the full-page AuthGateInteraction.
 */

import type { DXCUserInteraction } from "dexie-cloud-addon"
import {
  Dialog,
  DialogContent,
  DialogOverlay,
} from "@/app/components/ui/dialog"
import { MessageAlert } from "./MessageAlert"
import { LogoutConfirmation } from "./LogoutConfirmation"
import { GenericInteraction } from "./GenericInteraction"

interface InAppInteractionOverlayProps {
  interaction: DXCUserInteraction
}

export function InAppInteractionOverlay({
  interaction,
}: InAppInteractionOverlayProps) {
  const { type, fields, onSubmit, onCancel, cancelLabel, submitLabel, alerts } =
    interaction

  // The dialog is always open when this component is rendered.
  // onOpenChange is a no-op — user must dismiss via the interaction's
  // own buttons (onSubmit / onCancel). We don't want ESC or backdrop
  // click to silently dismiss a data-safety dialog.

  const content = (() => {
    switch (type) {
      case "message-alert":
        return (
          <MessageAlert
            alerts={alerts}
            onSubmit={onSubmit}
            onCancel={onCancel}
            submitLabel={submitLabel}
            cancelLabel={cancelLabel}
          />
        )
      case "logout-confirmation":
        return (
          <LogoutConfirmation
            alerts={alerts}
            onSubmit={onSubmit}
            onCancel={onCancel}
            submitLabel={submitLabel}
            cancelLabel={cancelLabel}
          />
        )
      case "generic":
        return (
          <GenericInteraction
            fields={fields}
            onSubmit={onSubmit}
            onCancel={onCancel}
            alerts={alerts}
            submitLabel={submitLabel}
            cancelLabel={cancelLabel}
          />
        )
      default:
        return null
    }
  })()

  if (!content) return null

  return (
    <Dialog open modal>
      <DialogOverlay className="bg-black/50" />
      <DialogContent
        className="max-w-sm border-none bg-transparent p-0 shadow-none [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Re-use existing components but strip their min-h-screen wrapper
            by overriding via a nested container that constrains height */}
        <div className="[&>div]:min-h-0 [&>div]:bg-transparent [&>div]:p-0 [&>div>div]:shadow-lg">
          {content}
        </div>
      </DialogContent>
    </Dialog>
  )
}
